import { createAction, props } from '@ngrx/store';
import { EmailVerificationCredentials, LoginCredentials } from '@uzurv/rideWss/app/models';
import { AuthData } from '@uzurv/rideWss/auth';
import { Address, DeviceInstallation, RegistrationRider, Rider, RiderDetail } from '@uzurv/rideWss/clara';
import { MobilityProfileDetail } from '@uzurv/rideWss/clara/models/mobility-profile-detail';
import { RiderAuthenticationState } from './rider-authentication.state';

export const registerRider = createAction('[RiderAuthentication] Register Rider', props<{ registrationData: RegistrationRider }>());

export const registerRiderSuccess = createAction(
  '[RiderAuthentication] Register Rider Success',
  props<{ registrationData: RegistrationRider }>()
);

export const registerRiderFailure = createAction('[RiderAuthentication] Register Rider Failure', props<{ error: any }>());

export const resendVerificationEmail = createAction('[RiderAuthentication] Resend Verification Email', props<{ emailAddress: string }>());

export const resendVerificationEmailSuccess = createAction(
  '[RiderAuthentication] Resend Verification Email Success',
  props<{ emailAddress: string }>()
);

export const resendVerificationEmailFailure = createAction(
  '[RiderAuthentication] Resend Verification Email Failure',
  props<{ error: any }>()
);

export const forgotPassword = createAction('[RiderAuthentication] Forgot Password', props<{ emailAddress: string }>());

export const forgotPasswordSuccess = createAction('[RiderAuthentication] Forgot Password Success', props<{ emailAddress: string }>());

export const forgotPasswordFailure = createAction('[RiderAuthentication] Forgot Password Failure', props<{ error: any }>());

export const authenticateRider = createAction('[RiderAuthentication] Authenticate Rider', props<{ loginCredentials: LoginCredentials }>());

export const authenticateRiderSuccess = createAction(
  '[RiderAuthentication] Authenticate Rider Success',
  props<{ loginCredentials: LoginCredentials; authData: AuthData }>()
);

export const riderAuthenticationComplete = createAction(
  '[RiderAuthentication] Rider Authentication Complete',
  props<{ rider: Rider; isTokenRefresh?: boolean }>()
);

export const storeLoginCredentials = createAction(
  '[RiderAuthentication] Store Login Credentials',
  props<{ loginCredentials: LoginCredentials }>()
);

export const logout = createAction('[RiderAuthentication] Logout');

export const logoutSuccess = createAction('[RiderAuthentication] Logout Success');

export const clearUserData = createAction('[RiderAuthentication] Clear User Data');

export const resetStore = createAction('[RiderAuthentication] Reset Store');

export const fetchRiderAddress = createAction('[RiderAuthentication] Fetch rider address');

export const fetchRiderAddressSuccess = createAction('[RiderAuthentication] Fetch rider address success', props<{ address: Address }>());

export const fetchRiderAddressFail = createAction('[RiderAuthentication] Fetch rider address fail', props<{ error: Error }>());

export const addRiderAddress = createAction('[RiderAuthentication] Add rider address', props<{ personId: number; address: Address }>());

export const addRiderAddressSuccess = createAction('[RiderAuthentication] Add rider address success', props<{ address: Address }>());

export const addRiderAddressFail = createAction('[RiderAuthentication] Add rider address fail', props<{ error: Error }>());

export const authenticateRiderFailure = createAction(
  '[RiderAuthentication] Authenticate Rider Failure',
  props<{
    loginCredentials: LoginCredentials;
    error: Error;
  }>()
);

export const refreshAuthenticatedRider = createAction(
  '[RiderAuthentication] Refresh Authenticated Rider',
  props<{ isFromAuthentication: boolean }>()
);

export const refreshAuthenticatedRiderSuccess = createAction(
  '[RiderAuthentication] Refresh Authenticated Rider Success',
  props<{ rider: RiderDetail; isFromAuthentication: boolean }>()
);

export const refreshAuthenticatedRiderFailure = createAction(
  '[RiderAuthentication] Refresh Authenticated Rider Failure',
  props<{ error: any; isFromAuthentication: boolean }>()
);

export const updateRiderPhoneNumber = createAction(
  '[RiderAuthentication] Update Rider Phone Number',
  props<{ rider: RiderDetail; dialogMessage: string }>()
);

export const updateRiderPhoneNumberSuccess = createAction(
  '[RiderAuthentication] Update Rider Phone Number Success',
  props<{ rider: RiderDetail }>()
);

export const updateRiderPhoneNumberFailure = createAction(
  '[RiderAuthentication] Update Rider Phone Number Failure',
  props<{ error: any }>()
);

export const updateRiderName = createAction(
  '[RiderAuthentication] Update Rider Name',
  props<{ rider: RiderDetail; dialogMessage: string }>()
);

export const updateRiderNameSuccess = createAction('[RiderAuthentication] Update Rider Name Success', props<{ rider: RiderDetail }>());

export const updateRiderNameFailure = createAction('[RiderAuthentication] Update Rider Name Failure', props<{ error: any }>());

export const rehydrateState = createAction('[RiderAuthentication] Rehydrate State');

export const rehydrateStateSuccess = createAction(
  '[RiderAuthentication] Rehydrate State Success',
  props<{ state: RiderAuthenticationState }>()
);

export const setCurrentDeviceInstallation = createAction(
  '[RiderAuthentication] Set Current Device Installation',
  props<{ currentDeviceInstallation: DeviceInstallation }>()
);

export const checkEmailVerification = createAction('[RiderAuthentication] Check Email Verification');

export const checkEmailVerificationSuccess = createAction('[RiderAuthentication] Check Email Verification Success');

export const setEmailVerificationCredentials = createAction(
  '[RiderAuthentication] Set Authentication Credentials',
  props<{ emailVerificationCredentials: EmailVerificationCredentials }>()
);

export const fetchRiderMobilityProfileDetail = createAction('[RiderAuthentication] Fetch Rider Mobility Profile Detail');

export const fetchRiderMobilityProfileDetailSuccess = createAction(
  '[RiderAuthentication] Fetch Rider Mobility Profile Detail Success',
  props<{ mobilityProfileDetail: MobilityProfileDetail }>()
);

export const fetchRiderMobilityProfileDetailFailure = createAction(
  '[RiderAuthentication] Fetch Rider Mobility Profile Detail Failure',
  props<{ error: Error }>()
);

export const updateRiderMobilityProfileDetail = createAction(
  '[RiderAuthentication] Update Rider Mobility Profile Detail',
  props<{ mobilityProfileDetail: MobilityProfileDetail }>()
);

export const updateRiderMobilityProfileDetailSuccess = createAction(
  '[RiderAuthentication] Update Rider Mobility Profile Detail Success',
  props<{ mobilityProfileDetail: MobilityProfileDetail }>()
);

export const updateRiderMobilityProfileDetailFailure = createAction(
  '[RiderAuthentication] Update Rider Mobility Profile Detail Failure',
  props<{ error: Error }>()
);

export const verifyRiderMobilityProfileDetail = createAction(
  '[RiderAuthentication] Verify Rider Mobility Profile Detail',
  props<{ mobilityProfileDetail: MobilityProfileDetail }>()
);

export const verifyRiderMobilityProfileDetailSuccess = createAction(
  '[RiderAuthentication] Verify Rider Mobility Profile Detail Success',
  props<{ mobilityProfileDetail: MobilityProfileDetail }>()
);

export const verifyRiderMobilityProfileDetailFailure = createAction(
  '[RiderAuthentication] Verify Rider Mobility Profile Detail Failure',
  props<{ error: Error }>()
);

export const updateModifiedMobilityProfileAids = createAction(
  '[RiderAuthentication] Update Modified Rider Mobility Profile Aids',
  props<{ mobilityProfileDetail: MobilityProfileDetail }>()
);

export const updateModifiedMobilityProfileAssistance = createAction(
  '[RiderAuthentication] Update Modified Rider Mobility Profile Assistance',
  props<{ mobilityProfileDetail: MobilityProfileDetail }>()
);

export const updateModifiedMobilityProfileMedicalHealth = createAction(
  '[RiderAuthentication] Update Modified Rider Mobility Profile Medical Health',
  props<{ mobilityProfileDetail: MobilityProfileDetail }>()
);

export const refreshModifiedMobilityProfileDetail = createAction('[RiderAuthentication] Refresh Rider Modified Mobility Profile Detail');
